import { useEffect, useState } from 'preact/hooks';
import { api } from '../../api/client';
import type { AccountDto, AccountSocialLinkDto } from '../../api/types';
import { Eyebrow, ErrorTile } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { useTranslation } from '../../i18n';

/**
 * Profile section of /me/account. Three groups:
 *   • Handle (slug) - change rate-limited per ADR-0028 (first change
 *     free, subsequent require a 24h cooldown).
 *   • Display name + bio.
 *   • Social links (up to 8 entries, each kind + handle + url).
 *
 * <p>Each group is its own form so a save error in one section doesn't
 * block the others. The whole page hydrates from a single GET and
 * patches the local state in place on every successful save.</p>
 */
export function AccountProfilePage() {
    const t = useTranslation();
    const [account, setAccount] = useState<AccountDto | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const toast = useToast();

    useEffect(() => {
        let cancelled = false;
        api.me.account
            .get()
            .then((a) => {
                if (!cancelled) setAccount(a);
            })
            .catch((e) => {
                if (!cancelled)
                    setLoadError(e instanceof Error ? e.message : String(e));
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <AccountShell
            title={t('me.profile.title')}
            subtitle={t('me.profile.subtitle')}
        >
            <div class="mx-auto w-full max-w-2xl space-y-10 px-[clamp(20px,3.5vw,56px)] py-10">
                {loadError && (
                    <ErrorTile title={t('me.profile.loadError')} details={loadError} />
                )}
                {!account && !loadError && (
                    <p class="text-sm text-fg-muted">{t('me.profile.loading')}</p>
                )}
                {account && (
                    <>
                        <SlugForm
                            account={account}
                            onChange={setAccount}
                            toast={toast}
                        />
                        <ProfileForm
                            account={account}
                            onChange={setAccount}
                            toast={toast}
                        />
                        <SocialLinksForm
                            account={account}
                            onChange={setAccount}
                            toast={toast}
                        />
                    </>
                )}
            </div>
        </AccountShell>
    );
}

// -- Slug -----------------------------------------------------------

function SlugForm({
    account,
    onChange,
    toast,
}: {
    account: AccountDto;
    onChange: (a: AccountDto) => void;
    toast: ReturnType<typeof useToast>;
}) {
    const t = useTranslation();
    const [slug, setSlug] = useState(account.slug);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cooldown = computeSlugCooldown(account);

    const onSubmit = async (e: SubmitEvent) => {
        e.preventDefault();
        if (slug === account.slug) return;
        setBusy(true);
        setError(null);
        try {
            const next = await api.me.account.setSlug(slug.trim().toLowerCase());
            onChange(next);
            setSlug(next.slug);
            toast.push('success', <>{t('me.profile.slug.saved')}</>);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setError(msg);
            toast.push('error', t('me.profile.slug.saveError', { msg }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section>
            <Eyebrow class="mb-3">{t('me.profile.slug.eyebrow')}</Eyebrow>
            <form onSubmit={onSubmit} class="space-y-3">
                <label class="block">
                    <span class="block text-sm text-fg-primary">
                        {t('me.profile.slug.label')}
                    </span>
                    <div class="mt-1 flex items-center gap-2">
                        <span class="font-mono text-sm text-fg-muted">@</span>
                        <input
                            type="text"
                            value={slug}
                            onInput={(e) =>
                                setSlug((e.target as HTMLInputElement).value)
                            }
                            disabled={busy || cooldown.locked}
                            class="flex-1 rounded border border-rule bg-surface-raised px-3 py-1.5 font-mono text-sm text-fg-primary outline-none focus:border-accent disabled:opacity-50"
                            pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                            minLength={3}
                            maxLength={32}
                            required
                        />
                    </div>
                </label>
                <p class="text-xs text-fg-muted">
                    {t('me.profile.slug.hint')}
                </p>
                {cooldown.locked && cooldown.nextAt && (
                    <p class="text-xs text-state-stale">
                        {t('me.profile.slug.cooldownPrefix')}{' '}
                        <time dateTime={cooldown.nextAt.toISOString()}>
                            {cooldown.nextAt.toLocaleString()}
                        </time>
                        .
                    </p>
                )}
                {error && <p class="text-xs text-state-failed">{error}</p>}
                <div>
                    <button
                        type="submit"
                        disabled={busy || cooldown.locked || slug === account.slug}
                        class="rounded border border-accent bg-accent px-4 py-1.5 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {busy ? t('me.profile.saving') : t('me.profile.slug.saveCta')}
                    </button>
                </div>
            </form>
        </section>
    );
}

function computeSlugCooldown(account: AccountDto): {
    locked: boolean;
    nextAt: Date | null;
} {
    if (account.slugChangeCount === 0) return { locked: false, nextAt: null };
    if (!account.slugSetAt) return { locked: false, nextAt: null };
    const next = new Date(Date.parse(account.slugSetAt) + 24 * 60 * 60 * 1000);
    return next > new Date() ? { locked: true, nextAt: next } : { locked: false, nextAt: null };
}

// -- Display name + bio --------------------------------------------

function ProfileForm({
    account,
    onChange,
    toast,
}: {
    account: AccountDto;
    onChange: (a: AccountDto) => void;
    toast: ReturnType<typeof useToast>;
}) {
    const t = useTranslation();
    const [displayName, setDisplayName] = useState(account.displayName ?? '');
    const [bio, setBio] = useState(account.bio ?? '');
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e: SubmitEvent) => {
        e.preventDefault();
        setBusy(true);
        try {
            const next = await api.me.account.updateProfile({
                displayName: displayName.trim() || null,
                bio: bio.trim() || null,
            });
            onChange(next);
            toast.push('success', <>{t('me.profile.about.saved')}</>);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', t('me.profile.about.saveError', { msg }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section>
            <Eyebrow class="mb-3">{t('me.profile.about.eyebrow')}</Eyebrow>
            <form onSubmit={onSubmit} class="space-y-4">
                <label class="block">
                    <span class="block text-sm text-fg-primary">{t('me.profile.about.displayName')}</span>
                    <input
                        type="text"
                        value={displayName}
                        onInput={(e) =>
                            setDisplayName((e.target as HTMLInputElement).value)
                        }
                        maxLength={64}
                        class="mt-1 w-full rounded border border-rule bg-surface-raised px-3 py-1.5 text-sm text-fg-primary outline-none focus:border-accent"
                        placeholder={t('me.profile.about.displayNamePlaceholder')}
                    />
                </label>
                <label class="block">
                    <span class="block text-sm text-fg-primary">{t('me.profile.about.bio')}</span>
                    <textarea
                        value={bio}
                        onInput={(e) =>
                            setBio((e.target as HTMLTextAreaElement).value)
                        }
                        maxLength={2000}
                        rows={5}
                        class="mt-1 w-full rounded border border-rule bg-surface-raised px-3 py-2 text-sm text-fg-primary outline-none focus:border-accent"
                        placeholder={t('me.profile.about.bioPlaceholder')}
                    />
                    <span class="mt-1 block text-xs text-fg-muted">
                        {bio.length} / 2000
                    </span>
                </label>
                <div>
                    <button
                        type="submit"
                        disabled={busy}
                        class="rounded border border-accent bg-accent px-4 py-1.5 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary disabled:opacity-50"
                    >
                        {busy ? t('me.profile.saving') : t('me.profile.about.saveCta')}
                    </button>
                </div>
            </form>
        </section>
    );
}

// -- Social links --------------------------------------------------

const SOCIAL_KINDS = [
    'discord',
    'twitter',
    'bluesky',
    'mastodon',
    'twitch',
    'youtube',
    'github',
    'ko-fi',
    'patreon',
    'website',
    'other',
];

function SocialLinksForm({
    account,
    onChange,
    toast,
}: {
    account: AccountDto;
    onChange: (a: AccountDto) => void;
    toast: ReturnType<typeof useToast>;
}) {
    const t = useTranslation();
    const [links, setLinks] = useState<AccountSocialLinkDto[]>(account.socialLinks);
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e: SubmitEvent) => {
        e.preventDefault();
        setBusy(true);
        try {
            const next = await api.me.account.updateProfile({
                socialLinks: links,
            });
            onChange(next);
            setLinks(next.socialLinks);
            toast.push('success', <>{t('me.profile.social.saved')}</>);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', t('me.profile.social.saveError', { msg }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section>
            <Eyebrow class="mb-3">
                {t('me.profile.social.eyebrow')}{' '}
                <span class="text-fg-faint">
                    ({links.length} / 8)
                </span>
            </Eyebrow>
            <form onSubmit={onSubmit} class="space-y-3">
                {links.length === 0 && (
                    <p class="text-sm text-fg-muted">
                        {t('me.profile.social.empty')}
                    </p>
                )}
                {links.map((link, i) => (
                    <div key={i} class="flex flex-wrap items-center gap-2">
                        <select
                            value={link.kind}
                            onChange={(e) => {
                                const next = [...links];
                                next[i] = {
                                    ...next[i],
                                    kind: (e.target as HTMLSelectElement).value,
                                };
                                setLinks(next);
                            }}
                            class="rounded border border-rule bg-surface-raised px-2 py-1 text-sm text-fg-primary outline-none focus:border-accent"
                        >
                            {SOCIAL_KINDS.map((k) => (
                                <option value={k}>{k}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            value={link.handle}
                            placeholder={t('me.profile.social.handlePlaceholder')}
                            onInput={(e) => {
                                const next = [...links];
                                next[i] = {
                                    ...next[i],
                                    handle: (e.target as HTMLInputElement).value,
                                };
                                setLinks(next);
                            }}
                            maxLength={100}
                            class="w-32 rounded border border-rule bg-surface-raised px-2 py-1 text-sm text-fg-primary outline-none focus:border-accent"
                        />
                        <input
                            type="url"
                            value={link.url}
                            placeholder={t('me.profile.social.urlPlaceholder')}
                            onInput={(e) => {
                                const next = [...links];
                                next[i] = {
                                    ...next[i],
                                    url: (e.target as HTMLInputElement).value,
                                };
                                setLinks(next);
                            }}
                            class="flex-1 min-w-[200px] rounded border border-rule bg-surface-raised px-2 py-1 text-sm text-fg-primary outline-none focus:border-accent"
                        />
                        <button
                            type="button"
                            onClick={() =>
                                setLinks(links.filter((_, idx) => idx !== i))
                            }
                            class="rounded border border-rule px-2 py-1 text-xs text-fg-muted transition-colors hover:border-state-failed hover:text-state-failed"
                            aria-label={t('me.profile.social.removeAria')}
                        >
                            ×
                        </button>
                    </div>
                ))}
                <div class="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        disabled={links.length >= 8}
                        onClick={() =>
                            setLinks([
                                ...links,
                                { kind: 'website', handle: '', url: '' },
                            ])
                        }
                        class="rounded border border-rule-strong px-3 py-1.5 text-sm text-fg-primary transition-colors hover:border-accent disabled:opacity-50"
                    >
                        {t('me.profile.social.addCta')}
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        class="rounded border border-accent bg-accent px-4 py-1.5 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary disabled:opacity-50"
                    >
                        {busy ? t('me.profile.saving') : t('me.profile.social.saveCta')}
                    </button>
                </div>
            </form>
        </section>
    );
}
